import { useEffect, useState } from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { Card, CardHeader } from '../components/ui/Card';
import { Field } from '../components/ui/Field';
import { Button } from '../components/ui/Button';
import { Badge, Dot } from '../components/ui/Badge';
import { Skeleton } from '../components/ui/Skeleton';
import { Settings as SettingsApi, Auth } from '../api/client';
import { Plug, Key, Cpu, Image as ImageIcon, ShieldCheck, AlertCircle } from 'lucide-react';
import { useStore } from '../store';

export function Settings() {
  const [data, setData] = useState<any>(null);
  const [auth, setAuth] = useState<any>(null);
  const [connecting, setConnecting] = useState(false);
  const pushToast = useStore((s) => s.pushToast);

  const load = () => {
    Promise.all([SettingsApi.get(), Auth.status()]).then(([d, a]) => {
      setData(d);
      setAuth(a);
    });
  };
  useEffect(load, []);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const { url } = await Auth.start();
      window.open(url, '_blank', 'noopener');
      pushToast({ kind: 'info', message: 'Etsy OAuth opened in a new tab. Approve and return.' });
    } catch (e) {
      pushToast({ kind: 'error', message: (e as Error).message });
    } finally {
      setConnecting(false);
    }
  };

  const env = data?.env ?? {};
  const keys = [
    { k: 'ETSY_API_KEY', set: env.etsy_key_set, hint: 'Get one at etsy.com/developers/register' },
    { k: 'ETSY_API_SECRET', set: env.etsy_secret_set, hint: 'Pair to your keystring' },
    { k: 'ETSY_DASHBOARD_MASTER_KEY', set: env.etsy_master_key_set, hint: 'Encrypts OAuth tokens in SQLite' },
    { k: 'MINIMAX_API_KEY', set: env.minimax_key_set, hint: 'minimax.io M3 access' },
    { k: 'MINIMAX_BASE_URL', set: env.minimax_base_set, hint: 'Override if self-hosted' },
  ];

  return (
    <div>
      <PageHeader
        kicker="// settings · 08"
        title="Configuration"
        description="Etsy auth, providers, content defaults. All settings live in .env + SQLite."
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Connection */}
        <Card noise>
          <CardHeader
            kicker="// shop"
            title="Etsy connection"
            description="OAuth 2.0 PKCE flow. Tokens encrypted with AES-256-GCM."
            actions={auth ? <Badge tone={auth.connected ? 'success' : 'warn'}>{auth.connected ? 'connected' : 'disconnected'}</Badge> : <Skeleton className="h-5 w-20" />}
          />
          <div className="space-y-3">
            <div className="surface-soft p-3.5 flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-etsy-100 to-amber/20 flex items-center justify-center border border-cream-300">
                <Plug size={15} className="text-etsy" strokeWidth={1.8} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-ink-800 tracking-tight font-medium">
                  {auth?.connected ? auth.shop_name : 'Not connected'}
                </div>
                <div className="kicker mt-0.5 text-ink-500">
                  {auth?.connected ? `shop_id · ${auth.shop_id}` : 'click connect to start OAuth'}
                </div>
              </div>
              {auth?.connected && <Badge tone="success">live</Badge>}
            </div>

            {!auth?.connected && (
              <div className="surface-soft p-3 border-amber/30 bg-amber/5 flex items-start gap-2.5">
                <AlertCircle size={14} className="text-amber shrink-0 mt-0.5" />
                <div className="text-[11.5px] text-ink-700 leading-relaxed">
                  Set <code className="font-mono text-amber">ETSY_API_KEY</code> and <code className="font-mono text-amber">ETSY_API_SECRET</code> in <code className="font-mono">.env</code> first, then click connect.
                </div>
              </div>
            )}

            <div className="flex items-center gap-2">
              <Button variant="primary" onClick={handleConnect} loading={connecting} leftIcon={<Plug size={13} />}>
                {auth?.connected ? 'Reconnect' : 'Connect to Etsy'}
              </Button>
              {auth?.connected && (
                <Button
                  variant="danger"
                  onClick={async () => {
                    await Auth.disconnect();
                    load();
                    pushToast({ kind: 'info', message: 'Disconnected' });
                  }}
                >
                  Disconnect
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Image provider */}
        <Card>
          <CardHeader
            kicker="// image-gen"
            title="Image provider"
            description="Hero image generation for new drafts."
            actions={<Badge tone="warn">stub</Badge>}
          />
          <div className="grid grid-cols-2 gap-2">
            {[
              { id: 'stub', name: 'Local stub', desc: 'Pre-rendered placeholders' },
              { id: 'codex', name: 'Codex Image 2', desc: 'minimax M3 image gen' },
            ].map((p) => (
              <button
                key={p.id}
                className={
                  'p-3.5 rounded-xl border text-left transition ' +
                  (p.id === 'stub'
                    ? 'border-etsy bg-etsy-50/60 shadow-[0_0_0_3px_rgba(241,100,30,0.08)]'
                    : 'border-cream-300 bg-white hover:border-etsy-200')
                }
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <ImageIcon size={11} className="text-etsy" />
                  <span className="text-[12.5px] text-ink-800 tracking-tight font-medium">{p.name}</span>
                  {p.id === 'stub' && <Badge tone="brand">active</Badge>}
                </div>
                <div className="text-[11.5px] text-ink-500">{p.desc}</div>
              </button>
            ))}
          </div>
          <div className="mt-3 kicker text-ink-500">// to enable codex: set OPENAI_API_KEY + provider in .env</div>
        </Card>

        {/* Copy provider */}
        <Card>
          <CardHeader
            kicker="// copy-gen"
            title="Copy provider"
            description="Title / description / tag generation."
            actions={<Badge tone="success">live</Badge>}
          />
          <div className="surface-soft p-3.5 flex items-center gap-3 mb-3">
            <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-etsy-100 to-etsy-200 flex items-center justify-center border border-etsy-200">
              <Cpu size={13} className="text-etsy" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[12.5px] text-ink-800 tracking-tight font-medium">minimax M3</div>
              <div className="kicker mt-0.5 text-ink-500">via MINIMAX_BASE_URL · {env.minimax_model ?? 'MiniMax-M3'}</div>
            </div>
            <Badge tone={env.minimax_key_set ? 'success' : 'warn'}>{env.minimax_key_set ? 'key set' : 'key missing'}</Badge>
          </div>
          <Field kicker="// model" label="Model" hint="Override per generation. Default: MiniMax-M3">
            <input defaultValue={env.minimax_model ?? 'MiniMax-M3'} className="font-mono" />
          </Field>
        </Card>

        {/* Content defaults */}
        <Card noise>
          <CardHeader kicker="// content" title="Content defaults" description="Brand voice and taxonomy hints." />
          <div className="space-y-3">
            <Field kicker="// voice" label="Brand voice" hint="3-5 words. Used in every prompt.">
              <input defaultValue="warm, handcrafted, premium" />
            </Field>
            <Field kicker="// path" label="Default taxonomy path" hint="Etsy category breadcrumb">
              <input defaultValue="Home & Living" />
            </Field>
            <Field kicker="// aspects" label="Aspect ratios" hint="Comma-separated. Default 1:1,4:5,3:4.">
              <input defaultValue="1:1,4:5,3:4" className="font-mono" />
            </Field>
            <Button variant="secondary" leftIcon={<ShieldCheck size={12} />}>
              Save defaults
            </Button>
          </div>
        </Card>
      </div>

      {/* Env keys */}
      <Card className="mt-4">
        <CardHeader kicker="// env" title="API keys" description="Read from .env. Values never leave the server." />
        <div className="space-y-1">
          {keys.map((k) => (
            <div key={k.k} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-cream-50/60 transition">
              <Key size={12} className="text-ink-400" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-[12px] text-ink-800">{k.k}</div>
                <div className="text-[11px] text-ink-500">{k.hint}</div>
              </div>
              <div className="flex items-center gap-1.5">
                <Dot tone={k.set ? 'success' : 'warn'} />
                <span className="kicker text-ink-500">{k.set ? 'set' : 'missing'}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
